import {
    AVAILABILITY_CALENDAR_DATE_FORMAT,
    TWENTY_FOUR_HOUR_TIME_FORMAT,
    DATE_HOUR_FORMAT,
    DATE_ONLY_FORMAT
} from "../config/DateTimeConstants";
import {lightenColor} from "./ColorUtils";
import {blue, grey} from "@mui/material/colors";
import {countPerformers} from "./PerformerMetricHelper";
import {SHIFT_SELECTION_STATUS} from "../domain/ShiftPlanConstants";

const moment = require("moment-timezone");

export const SHIFT_PLAN_STATUS_COLORS = {
    PERFORMER_SELECTED_DARK_BLUE_COLOR: blue[700],
    PERFORMER_NOT_AVAILABLE_COLOR: grey[400],
    GENERATED_SHIFT_DARK_GOLD_COLOR: '#CC9933'
};

const OVERLAY_STRIPE_COLOR = '#939393';
export const UNBOOKABLE_AND_UNACCREDITED_SERVICE_TYPE_PATTERN = `repeating-linear-gradient(45deg, ${OVERLAY_STRIPE_COLOR} 0,
    ${OVERLAY_STRIPE_COLOR} 0.08rem, transparent 0.1rem, transparent 0.4rem)`;

export const SHIFT_PLAN_ERROR_MESSAGE = {
    SHIFT_ALREADY_GENERATED: 'Cannot unenroll performer for whom shift is already generated.',
    SHIFT_ALREADY_HAS_SESSION: 'Cannot delete the shift as there is a session within the shift.',
    SHIFT_IS_IN_PAST: 'Cannot enroll/unenroll from past shift plans.',
    SHIFT_IS_DELETED: 'Cannot enroll/unenroll performer from a deleted shift.',
    CANNOT_MODIFY_PAST_SHIFT_PLAN: 'Cannot select/deselect past shift plans.',
    PERFORMER_HAS_CONFLICTED_SHIFT_PLAN: 'Cannot select conflicted/already generated shift plans.',
    PERFORMER_NOT_AVAILABLE: 'Cannot select performer as they are not available.',
    NO_PERFORMERS_AVAILABLE: 'Add performers to execute shift plan actions.',
    PERFORMER_SHIFT_ALREADY_GENERATED: 'Shift has already been generated for the performer.',
    NO_SHIFT_PLANS_AVAILABLE: 'No shift plans available for the selected day.'   
};

export const SHIFT_PLAN_SUCCESS_MESSAGE = {
    SELECTION_UPDATED: 'Performer selection updated.',
    TIMEBLOCK_SELECTION_UPDATED: 'Timeblock selection updated.',
    PERFORMER_ENROLLED: 'Performer enrolled.',
    PERFORMER_UNENROLLED: 'Performer unenrolled.',
    PERFORMER_ENROLLMENT_UPDATED: 'Performer enrollment updated.',
    SHIFTS_GENERATED: 'Shifts generated.',
    SHIFTS_UPDATED: 'Shifts updated.',
    ENROLLED_IN_ALL_SHIFTS: 'Enrolled in all shifts.',
    UNENROLLED_FROM_ALL_SHIFTS: 'Unenrolled from all shifts.',
    SHIFT_PLANS_DELETED: 'Shift plans deleted.'
};

export const EVENT_TYPE = {
    SHIFT_PLAN: 'SHIFT_PLAN',
    SHIFT: 'SHIFT',
    ALL_DAY: 'ALL_DAY'
};

export const DAY_DIFFERENCE_LABELS = {
    NEXT_DAY: '(+1)',
    PREVIOUS_DAY: '(-1)',
};

const TIMEBLOCK_DURATION_IN_HOURS = 1;

export const buildShiftPlanMap = (shiftPlans) => {

    return shiftPlans.map(function (shiftPlan) {
        return {
            'id': shiftPlan.id,
            'start': shiftPlan.startTime,
            'end': shiftPlan.endTime,
            'bookableServices': shiftPlan.bookableServiceTypePlans.map(serviceTypePlans => serviceTypePlans.serviceType.keycode),
            'bookableServiceTypePlans': shiftPlan.bookableServiceTypePlans,
            'enrollments': shiftPlan.enrollments
        };
    });
};

export const buildCurrentEnrollments = (shiftPlans, shiftPlanId) => {
    const currentShiftPlan = shiftPlans.find((shiftPlan) => shiftPlan.id === shiftPlanId);
    return currentShiftPlan?.enrollments || [];
};

export const isUnableToUnenrollUser = (currentEnrollments, selectedPerformerIds) => {
    return currentEnrollments.some((enrollment) => !selectedPerformerIds.includes(enrollment.user.id) && enrollment.availability !== null);
};

export const buildUnenrolledPerformerIds = (currentEnrollments, selectedPerformerIds) => {
    return currentEnrollments
    .filter((enrollment) => !selectedPerformerIds.includes(enrollment.user.id))
    .map((enrollment) => enrollment.user.id);
};

export const buildEnrolledPerformerIds = (currentEnrollments, selectedPerformerIds) => {
    return selectedPerformerIds.filter((performerId) => !currentEnrollments.some((enrollment) => enrollment.user.id === performerId));
};

export const isShiftPlanAfterCurrentTime = (shiftPlanTime) => moment(shiftPlanTime).isAfter();

export const isTimeblockOverlappingWindow = (timeblock, windowStartTime, windowEndTime) => {
    if (!windowStartTime || !windowEndTime) {
        return false;
    }
    
    const timeblockStart = new Date(timeblock.fromTime);
    const timeblockEnd = new Date(timeblock.toTime);
    const windowStartDate = new Date(windowStartTime);
    const windowEndDate = new Date(windowEndTime);

    return timeblockStart < windowEndDate && timeblockEnd > windowStartDate;
};

export const sortEnrollmentsByPerformerNames = (enrollments = []) =>
    enrollments.slice().sort((a, b) => a.user.displayName.localeCompare(b.user.displayName));

export const constructShiftPlansAndShiftArrayForWeekView = (filteredShiftPlans, filteredShifts) => {
    let filteredEvents = [];

    if (filteredShifts) {
        filteredShifts.forEach(shift => {
            if (!shift.isDeleted) {
                filteredEvents.push(shift);
            }
        });
    }

    filteredShiftPlans?.forEach(shiftPlan => {

        if (!shiftPlan.isShiftGenerated) {
            filteredEvents.push(shiftPlan);
        }
    });

    const mergedEvents = mergeHourlyEvents(filteredEvents);

    return mergedEvents;
};

export const buildServiceTypeTooltip = (bookableServiceTypePlans) => {
    return bookableServiceTypePlans
        .slice()
        .map(plan => `${plan.serviceType.keycode}: ${moment(plan.startTime).format(TWENTY_FOUR_HOUR_TIME_FORMAT)} - ${moment(plan.endTime).format(TWENTY_FOUR_HOUR_TIME_FORMAT)}`)
        .join('\n');
};

const buildAvailableServiceTypes = (bookableServiceTypePlans) => {
    return bookableServiceTypePlans.map(serviceTypePlan => {
        const { startTime, endTime, serviceType } = serviceTypePlan;
        const { keycode, description, primaryColor } = serviceType;

        return {
            serviceTypeId: serviceTypePlan.id,
            serviceKeycode: keycode,
            serviceDescription: description,
            servicePrimaryColor: primaryColor,
            serviceStartTime: startTime,
            serviceEndTime: endTime,
            serviceStartHour: moment(startTime).format(TWENTY_FOUR_HOUR_TIME_FORMAT),
            serviceEndHour: moment(endTime).format(TWENTY_FOUR_HOUR_TIME_FORMAT)
        };
    });
};

const buildEnrolledPerformers = (enrollments) => {
    return enrollments.map(enrollment => ({
        performerId: enrollment.user.id,
        performerName: enrollment.user.displayName,
        enrollmentStatus: enrollment.shiftSelectionStatus,
        hasAvailability: enrollment.availability !== null,
        availabilityId: enrollment.availability?.id,
        hasTeachingSessions: enrollment.availability?.hasAnyTeachingSessions || false
    }));
};

export const buildTimeblockShiftPlanMap = (shiftPlans) => {
    return shiftPlans.map(function (shiftPlan) {
        const enrolledPerformers = buildEnrolledPerformers(shiftPlan.enrollments);

        const timeblockColors = {};
        shiftPlan.bookableServiceTypePlans.forEach(serviceTypePlan => {
            const {startTime, endTime, serviceType} = serviceTypePlan;
            const color = lightenColor(serviceType.primaryColor);

            const start = moment(startTime).startOf('hour');
            const end = moment(endTime);
            const current = moment(start);

            while (current.isBefore(end)) {
                const timeFormat = current.format(DATE_HOUR_FORMAT);

                if (!timeblockColors[timeFormat]) {
                    timeblockColors[timeFormat] = [];
                }

                timeblockColors[timeFormat].push(color);
                current.add(TIMEBLOCK_DURATION_IN_HOURS, 'hour');
            }
        });

        return {
            'shiftPlanId': shiftPlan.id,
            'shiftStartTime': shiftPlan.startTime,
            'shiftEndTime': shiftPlan.endTime,
            'availableServiceTypes': buildAvailableServiceTypes(shiftPlan.bookableServiceTypePlans),
            'enrolledPerformers': enrolledPerformers,
            'uniquePerformerNames': [...new Set(shiftPlan.enrollments.map(enrollment => enrollment.user.displayName))].sort(),
            'uniquePerformerEntries': Array.from(new Map(shiftPlan.enrollments.map(enrollment => [enrollment.user.id, {
                id: enrollment.user.id,
                name: enrollment.user.displayName,
                shift: enrollment.availability,
                fromTime: enrollment.fromTime,
                toTime: enrollment.toTime,
                approvedFromTime: enrollment.approvedFromTime,
                approvedToTime: enrollment.approvedToTime,
                shiftSelectionStatus: enrollment.shiftSelectionStatus
            }])).values()).sort((a, b) => a.name.localeCompare(b.name)),
            'timeblockColors': timeblockColors
        };
    });
};

export const sortPerformersByGeneratedShifts = (performers) => {
    return [...performers].sort((performerA, performerB) => {
        const performerAHasGeneratedShift = performerA?.shift != null;
        const performerBHasGeneratedShift = performerB?.shift != null;

        if (performerAHasGeneratedShift && !performerBHasGeneratedShift) {
            return -1;
        } else if (!performerAHasGeneratedShift && performerBHasGeneratedShift) {
            return 1;
        } else {
            return performerA.name.localeCompare(performerB.name);
        }
    });
};

export const buildPerformerEntriesFromShiftPlans = (processedShiftPlans, selectedDate) => {
    const performerEntriesFromAllShiftPlans = [];

    const selectedDayStart = moment(selectedDate).startOf('day');
    const selectedDayEnd = moment(selectedDate).endOf('day');
    
    processedShiftPlans.forEach(shiftPlan => {
        const {shiftStartTime, shiftEndTime, uniquePerformerEntries, shiftPlanId} = shiftPlan;

        if (selectedDate) {
        
            const shiftStart = moment(shiftStartTime);
            const shiftEnd = moment(shiftEndTime);    
            const hasOverlapWithSelectedDay = shiftStart.isBefore(selectedDayEnd) && shiftEnd.isAfter(selectedDayStart);
            
            if (!hasOverlapWithSelectedDay) {
                return;
            }
        }

        uniquePerformerEntries.forEach(({ id, name, shiftSelectionStatus, fromTime, toTime, approvedFromTime, approvedToTime, shift }) => {
            performerEntriesFromAllShiftPlans.push({
                id,
                name,
                shiftPlanId,
                shiftSelectionStatus,
                shiftStartTime,
                shiftEndTime,
                approvedFromTime,
                approvedToTime,
                enrollmentFromTime: fromTime,
                enrollmentToTime: toTime,
                shift
            });
        });
    });

    return sortPerformersByGeneratedShifts(performerEntriesFromAllShiftPlans);
};

export const calculateConstrainedTimeRange = (shiftPlanStartTime, shiftPlanEndTime, selectedDate) => {
    let constrainedStartTime = moment(shiftPlanStartTime);
    let constrainedEndTime = moment(shiftPlanEndTime);

    if (selectedDate) {
        const selectedDayStart = moment(selectedDate).startOf('day');
        constrainedStartTime = moment.max(constrainedStartTime, selectedDayStart);
    }

    return {
        constrainedStartHour: constrainedStartTime.hour(),
        constrainedEndHour: constrainedEndTime.hour()
    };
};

export function getOverlappingShiftPlanIds(shiftPlans, dayStart, dayEnd) {
    return Array.from(new Set(
        (shiftPlans || [])
            .filter(sp => {
                const start = moment(sp.start);
                const end = moment(sp.end);
                return start.isBefore(dayEnd) && end.isAfter(dayStart);
            })
            .map(sp => sp.id)
    ));
}

export const generateTimeBlocksFromShiftPlans = (processedShiftPlans, selectedDate) => {
    if (!processedShiftPlans.length || !selectedDate) {
        return [];
    }

    const selectedDayStart = moment(selectedDate).startOf('day');
    const selectedDayEnd = moment(selectedDate).endOf('day');

    const overlappingPlans = processedShiftPlans.filter(shiftPlan => {
        const start = moment(shiftPlan.shiftStartTime);
        const end = moment(shiftPlan.shiftEndTime);
        return start.isBefore(selectedDayEnd) && end.isAfter(selectedDayStart);
    });

    if (!overlappingPlans.length){
        return [];
    }

    const constrainedShiftPlanStartTimes = overlappingPlans.map(shiftPlan =>
        moment.max(moment(shiftPlan.shiftStartTime), selectedDayStart)
    );

    const unConstrainedShiftPlanEndTimes = overlappingPlans.map(shiftPlan =>
        moment(shiftPlan.shiftEndTime)
    );

    const earliestConstrainedStartTime = moment.min(constrainedShiftPlanStartTimes);
    const latestConstrainedEndTime = moment.max(unConstrainedShiftPlanEndTimes);

    const gridStartHourMoment = moment.max(earliestConstrainedStartTime.clone().startOf('hour'), selectedDayStart);

    const gridEndHourMoment = latestConstrainedEndTime.minute() === 0
        ? latestConstrainedEndTime.clone()
        : latestConstrainedEndTime.clone().add(1, 'hour').startOf('hour');

    const hourlyTimeBlocksForDisplay = [];
    const currentTimeBlockIterator = gridStartHourMoment.clone();

    while (currentTimeBlockIterator.isBefore(gridEndHourMoment)) {
        const currentHourOfDay = currentTimeBlockIterator.format(TWENTY_FOUR_HOUR_TIME_FORMAT);

        const blockStartTime = currentTimeBlockIterator.clone();
        const blockEndTime = currentTimeBlockIterator.clone().add(TIMEBLOCK_DURATION_IN_HOURS, 'hour');

        let actualStartTime = blockStartTime.format();
        let actualEndTime = blockEndTime.format();

        overlappingPlans.forEach(shiftPlan => {
            const shiftStart = moment(shiftPlan.shiftStartTime);
            const shiftEnd = moment(shiftPlan.shiftEndTime);

            if (shiftStart.isBetween(blockStartTime, blockEndTime, null, '[)')) {
                actualStartTime = shiftStart.format();
            }

            if (shiftEnd.isBetween(blockStartTime, blockEndTime, null, '(]')) {
                actualEndTime = shiftEnd.format();
            }
        });

        hourlyTimeBlocksForDisplay.push({
            displayTime: currentHourOfDay,
            fromTime: blockStartTime.format(),
            toTime: blockEndTime.format(),
            fromTimeUtc: blockStartTime.utc().format(),
            toTimeUtc: blockEndTime.utc().format(),
            actualStartTime,
            actualEndTime,
            actualStartTimeUtc: moment(actualStartTime).utc().format(),
            actualEndTimeUtc: moment(actualEndTime).utc().format()
        });
        currentTimeBlockIterator.add(TIMEBLOCK_DURATION_IN_HOURS, 'hour');
    }

    return hourlyTimeBlocksForDisplay;
};

export const splitIntoHourlyChunks = (startTime, endTime, timezone) => {
    const start = moment.tz(startTime, timezone);
    const end = moment.tz(endTime, timezone);
    const chunks = [];

    let current = start.clone();

    while (current.isBefore(end)) {
        // The next boundary is the next hour or the end time, whichever is earlier
        const nextHour = current.clone().add(1, 'hour').startOf('hour');
        const next = moment.min(nextHour, end);
        chunks.push([current.clone(), next.clone()]);
        current = next;
    }

    return chunks;
};

export const isWithinEnrollmentRange = (current, next, fromTime, toTime) => {
    if (!fromTime || !toTime) {
        return false;
    }

    return current.isSameOrAfter(fromTime) && next.isSameOrBefore(toTime);
};

function mergeHourlyEvents(events) {
    const hourMap = new Map();

    events.forEach(event => {
        const hourKey = moment(event.start).format(AVAILABILITY_CALENDAR_DATE_FORMAT);
        if (!hourMap.has(hourKey)) {
            hourMap.set(hourKey, { ...event });
        } else {
            const existing = hourMap.get(hourKey);
            hourMap.set(hourKey, {
                ...existing,
                serviceType: [
                    ...(Array.isArray(existing.serviceType) ? existing.serviceType : [existing.serviceType]),
                    ...(Array.isArray(event.serviceType) ? event.serviceType : [event.serviceType])
                ],
            });
        }
    });

    return Array.from(hourMap.values());
}

export const generateEmptyGrid = (shiftPlans = null) => {
    const emptyTimeblocks = [];
    const DEFAULT_START_HOUR = 8;
    const DEFAULT_END_HOUR = 14;
    const TIME_FORMAT_PAD_LENGTH = 2;
    const TIME_FORMAT_PAD_CHAR = '0';
    const HOUR_INCREMENT = 1;
    const EMPTY_PERFORMER_COUNT = 1;
    
    let startHour = DEFAULT_START_HOUR;
    let endHour = DEFAULT_END_HOUR;
    
    if (shiftPlans && shiftPlans.length > 0) {
        const shiftPlan = shiftPlans[0];
        if (shiftPlan.fromTime && shiftPlan.toTime) {
            startHour = moment(shiftPlan.fromTime).hour();
            endHour = moment(shiftPlan.toTime).hour();
            
            if (moment(shiftPlan.toTime).minute() > 0) {
                endHour += 1;
            }
        }
    }
    
    for (let hour = startHour; hour < endHour; hour++) {
        const startTimeFormatted = `${hour.toString().padStart(TIME_FORMAT_PAD_LENGTH, TIME_FORMAT_PAD_CHAR)}:00`;
        const endTimeFormatted = `${(hour + HOUR_INCREMENT).toString().padStart(TIME_FORMAT_PAD_LENGTH, TIME_FORMAT_PAD_CHAR)}:00`;
        
        emptyTimeblocks.push({
            startTime: startTimeFormatted,
            endTime: endTimeFormatted,
            displayTime: startTimeFormatted,
            id: `empty-${hour}`
        });
    }

    const emptyPerformers = Array.from({length: EMPTY_PERFORMER_COUNT}, (_, index) => ({
        id: `empty-performer-${index}`,
        name: '',
        enrollments: []
    }));

    return {
        timeblocks: emptyTimeblocks,
        performers: emptyPerformers
    };
};

export const calculateTimeOverlap = (timeblockStartTime, timeblockEndTime, performerStartTime, performerEndTime) => {
    const PERCENTAGE_MULTIPLIER = 100;

    const timeblockStart = new Date(timeblockStartTime);
    const timeblockEnd = new Date(timeblockEndTime);
    const timeblockDurationMs = timeblockEnd - timeblockStart;

    const startTime = performerStartTime ? new Date(performerStartTime) : null;
    const endTime = performerEndTime ? new Date(performerEndTime) : null;

    if (!startTime || !endTime) {
        return null;
    }

    const overlapStart = timeblockStart > startTime ? timeblockStart : startTime;
    const overlapEnd = timeblockEnd < endTime ? timeblockEnd : endTime;
    const hasOverlap = overlapStart < overlapEnd;

    const overlapStartPercent = ((overlapStart - timeblockStart) / timeblockDurationMs) * PERCENTAGE_MULTIPLIER;
    const overlapEndPercent = ((overlapEnd - timeblockStart) / timeblockDurationMs) * PERCENTAGE_MULTIPLIER;
    const overlapWidthPercent = overlapEndPercent - overlapStartPercent;

    return {
        timeblockStart,
        timeblockEnd,
        timeblockDurationMs,
        overlapStart,
        overlapEnd,
        hasOverlap,
        overlapStartPercent,
        overlapEndPercent,
        overlapWidthPercent
    };
};


const getFilteredServices = (services, performer) => {
    const filteredServiceTypes = services && services.length > 0 
        ? services.filter(serviceType => serviceType.shiftPlanId === performer.shiftPlanId)
        : [];
    
    return filteredServiceTypes;
};

const createBookableServiceColorRangeForTimeblock = (filteredServiceTypes, serviceOverlapInfo, statusOverlapInfos = []) => {
    const PERCENTAGE_MULTIPLIER = 100;

    const { timeblockStart, timeblockDurationMs, overlapStart: serviceOverlapStart, overlapEnd: serviceOverlapEnd } = serviceOverlapInfo;

    const bookableServiceStartAndEndTime = new Set();
    bookableServiceStartAndEndTime.add(serviceOverlapStart.getTime());
    bookableServiceStartAndEndTime.add(serviceOverlapEnd.getTime());

    statusOverlapInfos.forEach(statusInfo => {
        const {overlapStart: statusOverlapStart, overlapEnd: statusOverlapEnd} = statusInfo;
        if (statusOverlapStart >= serviceOverlapStart && statusOverlapStart <= serviceOverlapEnd) {
            bookableServiceStartAndEndTime.add(statusOverlapStart.getTime());
        }

        if (statusOverlapEnd >= serviceOverlapStart && statusOverlapEnd <= serviceOverlapEnd) {
            bookableServiceStartAndEndTime.add(statusOverlapEnd.getTime());
        }
    });
    
    filteredServiceTypes.forEach(service => {
        const serviceStart = new Date(service.serviceStartTime);
        const serviceEnd = new Date(service.serviceEndTime);
        
        if (serviceStart >= serviceOverlapStart && serviceStart <= serviceOverlapEnd) {
            bookableServiceStartAndEndTime.add(serviceStart.getTime());
        }

        if (serviceEnd >= serviceOverlapStart && serviceEnd <= serviceOverlapEnd) {
            bookableServiceStartAndEndTime.add(serviceEnd.getTime());
        }
    });
    
    const sortedBoundaryTimestamps = Array.from(bookableServiceStartAndEndTime).sort((a, b) => a - b);
    const colorRanges = [];
    
    for (let i = 0; i < sortedBoundaryTimestamps.length - 1; i++) {
        const rangeStartTime = new Date(sortedBoundaryTimestamps[i]);
        const rangeEndTime = new Date(sortedBoundaryTimestamps[i + 1]);
        
        const servicesActiveInRange = filteredServiceTypes.filter(service => {
            const serviceStart = new Date(service.serviceStartTime);
            const serviceEnd = new Date(service.serviceEndTime);
            
            return serviceStart < rangeEndTime && serviceEnd > rangeStartTime;
        });
        
        const startPercentInTimeblock = ((rangeStartTime - timeblockStart) / timeblockDurationMs) * PERCENTAGE_MULTIPLIER;
        const endPercentInTimeblock = ((rangeEndTime - timeblockStart) / timeblockDurationMs) * PERCENTAGE_MULTIPLIER;
        const widthPercentOfTimeblock = endPercentInTimeblock - startPercentInTimeblock;
        
        colorRanges.push({
            startPercentInTimeblock,
            widthPercentOfTimeblock,
            servicesActiveInRange
        });
    }
    
    return colorRanges;
};

export const isTimeRangeOverlapsWithApprovedTime = (rangeStartTime, rangeEndTime, statusOverlapInfo) => {
    if (!statusOverlapInfo || !statusOverlapInfo.hasOverlap) {
        return false;
    }

    const {overlapStart, overlapEnd} = statusOverlapInfo;

    return rangeStartTime < overlapEnd && rangeEndTime > overlapStart;
};

const getStatusColorForRange = (rangeStartTime, rangeEndTime, statusOverlapInfos) => {
    const matchingStatuses = statusOverlapInfos.filter(statusInfo =>
        isTimeRangeOverlapsWithApprovedTime(rangeStartTime, rangeEndTime, statusInfo)
    );

    if (matchingStatuses.length === 0) {
        return null;
    }

    return matchingStatuses[0].color;
};

const createTimeblockBackgroundGradients = (colorRanges, serviceOverlapInfo, notAvailableColor, accreditedServiceTypes = [], statusOverlapInfos = []) => {
    const FULL_PERCENTAGE = 100;
    const PERCENTAGE_MULTIPLIER = 100;
    const serviceTypeUnaccreditedPattern = UNBOOKABLE_AND_UNACCREDITED_SERVICE_TYPE_PATTERN;

    const { overlapStartPercent, overlapEndPercent } = serviceOverlapInfo;
    const backgroundLayers = [];
    const unAccreditedServiceOverlay = [];

    if (overlapStartPercent > 0) {
        backgroundLayers.push(`linear-gradient(to right, ${notAvailableColor} 0%, ${notAvailableColor} ${FULL_PERCENTAGE}%) 0% 0% / ${overlapStartPercent}% ${FULL_PERCENTAGE}% no-repeat`);
    }

    colorRanges.forEach(range => {
        const rangeStartTime = new Date(serviceOverlapInfo.timeblockStart.getTime() + (range.startPercentInTimeblock / PERCENTAGE_MULTIPLIER) * serviceOverlapInfo.timeblockDurationMs);
        const rangeEndTime = new Date(rangeStartTime.getTime() + (range.widthPercentOfTimeblock / PERCENTAGE_MULTIPLIER) * serviceOverlapInfo.timeblockDurationMs);

        const statusColor = getStatusColorForRange(rangeStartTime, rangeEndTime, statusOverlapInfos);

        if (statusColor) {
            const remainingSpaceFromStart = FULL_PERCENTAGE - range.startPercentInTimeblock;
            const cssWidth = remainingSpaceFromStart > 0 ? (range.widthPercentOfTimeblock / remainingSpaceFromStart) * PERCENTAGE_MULTIPLIER : range.widthPercentOfTimeblock;

            const statusGradient = `${statusColor} 0%, ${statusColor} ${FULL_PERCENTAGE}%`;
            backgroundLayers.push(`linear-gradient(to bottom, ${statusGradient}) ${range.startPercentInTimeblock}% 0% / ${cssWidth}% ${FULL_PERCENTAGE}% no-repeat`);
        } else if (range.servicesActiveInRange.length > 0) {
            const serviceColors = [];

            range.servicesActiveInRange.forEach((service, index) => {
                const isAccredited = accreditedServiceTypes.some(accreditedType =>
                    accreditedType.keycode === service.serviceKeycode
                );

                serviceColors.push(lightenColor(service.servicePrimaryColor));

                if (!isAccredited) {
                    const sectionHeight = FULL_PERCENTAGE / range.servicesActiveInRange.length;
                    unAccreditedServiceOverlay.push({
                        index: index,
                        sectionHeight: sectionHeight,
                        startPercent: range.startPercentInTimeblock,
                        widthPercent: range.widthPercentOfTimeblock,
                        pattern: serviceTypeUnaccreditedPattern
                    });
                }
            });

            const verticalGradient = serviceColors.map((color, i) =>
                `${color} ${(i / serviceColors.length) * PERCENTAGE_MULTIPLIER}%, ${color} ${((i + 1) / serviceColors.length) * PERCENTAGE_MULTIPLIER}%`
            ).join(', ');
            const remainingSpaceFromStart = FULL_PERCENTAGE - range.startPercentInTimeblock;
            const cssWidth = remainingSpaceFromStart > 0 ? (range.widthPercentOfTimeblock / remainingSpaceFromStart) * PERCENTAGE_MULTIPLIER : range.widthPercentOfTimeblock;

            backgroundLayers.push(`linear-gradient(to bottom, ${verticalGradient}) ${range.startPercentInTimeblock}% 0% / ${cssWidth}% ${FULL_PERCENTAGE}% no-repeat`);
        }
    });

    if (overlapEndPercent < FULL_PERCENTAGE) {
        const afterOverlapWidth = FULL_PERCENTAGE - overlapEndPercent;
        const remainingSpaceAfter = FULL_PERCENTAGE - overlapEndPercent;
        const cssWidthAfter = remainingSpaceAfter > 0 ? (afterOverlapWidth / remainingSpaceAfter) * PERCENTAGE_MULTIPLIER : afterOverlapWidth;
        backgroundLayers.push(`linear-gradient(to right, ${notAvailableColor} 0%, ${notAvailableColor} ${FULL_PERCENTAGE}%) ${overlapEndPercent}% 0% / ${cssWidthAfter}% ${FULL_PERCENTAGE}% no-repeat`);
    }

    return { backgroundLayers, unAccreditedServiceOverlay };
};

export const renderPartialServiceTimeblockBackground = (
    timeblockStartTime,
    timeblockEndTime,
    services,
    performer,
    performerStatusColors = [],
    accreditedServiceTypes = []
) => {
    const FULL_PERCENTAGE = 100;
    
    if (!performer) {
        return '';
    }
    
    const notAvailableColor = SHIFT_PLAN_STATUS_COLORS.PERFORMER_NOT_AVAILABLE_COLOR;

    const serviceOverlapInfo = calculateTimeOverlap(timeblockStartTime, timeblockEndTime, performer.enrollmentFromTime, performer.enrollmentToTime);

    const statusOverlapInfos = performerStatusColors
        .filter(statusColor => statusColor.startTime && statusColor.endTime)
        .map(statusColor => ({
            ...calculateTimeOverlap(timeblockStartTime, timeblockEndTime, statusColor.startTime, statusColor.endTime),
            color: statusColor.color
        }))
        .filter(info => info.hasOverlap);

    if (!serviceOverlapInfo || !serviceOverlapInfo.hasOverlap) {
        return `linear-gradient(to right, ${notAvailableColor} 0%, ${notAvailableColor} ${FULL_PERCENTAGE}%)`;
    }

    const filteredServiceTypes = getFilteredServices(services, performer);

    const colorRanges = createBookableServiceColorRangeForTimeblock(filteredServiceTypes, serviceOverlapInfo, statusOverlapInfos);

    const backgroundData = createTimeblockBackgroundGradients(
        colorRanges,
        serviceOverlapInfo,
        notAvailableColor,
        accreditedServiceTypes,
        statusOverlapInfos
    );

    return {
        background: backgroundData.backgroundLayers.join(', '),
        unaccreditedOverlays: backgroundData.unAccreditedServiceOverlay
    };
};

export const fetchOverlappingServiceTypes = (bookableServiceTypes, start, end) => {

    if (!bookableServiceTypes) {
        return [];
    }

    return bookableServiceTypes
        .filter(({ startTime, endTime }) =>
            moment(endTime).isAfter(start) && moment(startTime).isBefore(end)
        )
        .map(({ serviceType }) => serviceType);
};

export const mapShiftPlansForCalendar = (shiftPlans, timezone) => {
    if (!shiftPlans || !Array.isArray(shiftPlans)) {
        return [];
    }

    return shiftPlans.map(shiftPlan => {
        const formattedStartTime = moment(shiftPlan.startTime).tz(timezone).format(TWENTY_FOUR_HOUR_TIME_FORMAT);
        const formattedEndTime = moment(shiftPlan.endTime).tz(timezone).format(TWENTY_FOUR_HOUR_TIME_FORMAT);
        const serviceTypeKeycodes = shiftPlan.bookableServiceTypePlans?.map(serviceTypePlans => serviceTypePlans.serviceType.keycode) || [];
        const serviceTypeDescriptions = shiftPlan.bookableServiceTypePlans?.map(serviceTypePlans => serviceTypePlans.serviceType.description) || [];
        const shiftPlanTitle = `${formattedStartTime} - ${formattedEndTime}`;

        return {
            id: shiftPlan.id,
            start: moment(shiftPlan.startTime).tz(timezone).toDate(),
            end: moment(shiftPlan.endTime).tz(timezone).toDate(),
            title: shiftPlanTitle,
            serviceTypeKeycodes,
            serviceTypeDescriptions,
            enrollments: shiftPlan.enrollments || []
        };
    });
};

export const groupShiftPlansByDate = (mappedShiftPlans, timezone) => {
    const shiftPlansByDateMap = new Map();
    
    if (!mappedShiftPlans || !Array.isArray(mappedShiftPlans)) {
        return shiftPlansByDateMap;
    }

    mappedShiftPlans.forEach(shiftPlan => {
        const dateKey = moment(shiftPlan.start).tz(timezone).format(DATE_ONLY_FORMAT);
        if (!shiftPlansByDateMap.has(dateKey)) {
            shiftPlansByDateMap.set(dateKey, []);
        }
        shiftPlansByDateMap.get(dateKey).push(shiftPlan);
    });

    return shiftPlansByDateMap;
};

export const computePerformerMetricsByDate = (shiftPlansByDateMap) => {
    const preComputedPerformerMetrics = new Map();
    
    if (!shiftPlansByDateMap || !(shiftPlansByDateMap instanceof Map)) {
        return preComputedPerformerMetrics;
    }

    shiftPlansByDateMap.forEach((shiftPlansForDate, dateKey) => {
        const performerMetrics = countPerformers(shiftPlansForDate);
        preComputedPerformerMetrics.set(dateKey, performerMetrics);
    });

    return preComputedPerformerMetrics;
};

export const processShiftPlanData = (data, timezone) => {
    if (!data || !data.shiftPlans) {
        return {
            mappedShiftPlans: [],
            shiftPlansByDateMap: new Map(),
            performerMetricsByDate: new Map()
        };
    }

    const mappedShiftPlans = mapShiftPlansForCalendar(data.shiftPlans, timezone);
    const shiftPlansByDateMap = groupShiftPlansByDate(mappedShiftPlans, timezone);
    const performerMetricsByDate = computePerformerMetricsByDate(shiftPlansByDateMap);

    return {
        mappedShiftPlans,
        shiftPlansByDateMap,
        performerMetricsByDate
    };
};

export const determineIsPerformerSelected = (performer) => {
    const isShiftAlreadyGenerated =
        moment(performer.approvedFromTime).isAfter(moment(performer.enrollmentFromTime)) ||
        moment(performer.approvedToTime).isBefore(moment(performer.enrollmentToTime));

    const isAlreadyGeneratedShiftSelected =
        moment(performer.shift?.performingFrom).isAfter(moment(performer.approvedFromTime)) ||
        moment(performer.shift?.to).isBefore(moment(performer.approvedFromTime));

    let isPerformerSelected = true;

    if (performer.shift !== null && isShiftAlreadyGenerated) {
        isPerformerSelected = true;
    } else if (performer.shift !== null && isAlreadyGeneratedShiftSelected) {
        isPerformerSelected = false;
    } else if (performer.shiftSelectionStatus === SHIFT_SELECTION_STATUS.SELECTED) {
        isPerformerSelected = false;
    }

    return isPerformerSelected;
};

export const formatDisplayTime = (timeblock, selectedDate) => {
    const timeblockDay = moment(timeblock.fromTime).startOf('day');
    const selectedDay = moment(selectedDate).startOf('day');

    if (timeblockDay.isAfter(selectedDay, 'day')) {
        return `${timeblock.displayTime}${DAY_DIFFERENCE_LABELS.NEXT_DAY}`;
    }

    return timeblock.displayTime;
};

export const formatTimeToUtc = (time) => {
  return moment(time).utc().format();
};

const calculateHoursFromDatesWithoutRounding = (startDate, endDate) => {
    const ALLOW_FLOATING_POINT_PRECISION = true;
    return moment(endDate).diff(moment(startDate), 'hours', ALLOW_FLOATING_POINT_PRECISION);
};

const roundToNearestHalfHour = (hours) => {
    const HALF_HOUR_INCREMENT = 0.5;
    return Math.round(hours / HALF_HOUR_INCREMENT) * HALF_HOUR_INCREMENT;
};

const calculateHoursFromDates = (startDate, endDate) => {
    const rawHours = calculateHoursFromDatesWithoutRounding(startDate, endDate);
    return roundToNearestHalfHour(rawHours);
};

export const calculateScheduledAndAvailableHours = (performer) => {
    const enrollmentStartTime = new Date(performer.enrollmentFromTime);
    const enrollmentEndTime = new Date(performer.enrollmentToTime);
    const availableHours = calculateHoursFromDates(enrollmentStartTime, enrollmentEndTime);

    let scheduledHours = 0;

    if (performer.shift !== null) {
        const performingFrom = new Date(performer.shift?.performingFrom);
        const performingTo = new Date(performer.shift?.to);
        scheduledHours = calculateHoursFromDates(performingFrom, performingTo);
    }

    return `${scheduledHours}/${availableHours}`;
};

export const calculateTimeblockScheduledAndAvailableHours = (timeblock, performerEnrollments) => {
    const timeblockStartTime = moment.utc(timeblock.fromTimeUtc);
    const timeblockEndTime = moment.utc(timeblock.toTimeUtc);

    let totalAvailableHours = 0;
    let totalScheduledHours = 0;

    for (const enrollment of performerEnrollments) {
        const enrollmentStartTime = moment.utc(enrollment.enrollmentFromTime);
        const enrollmentEndTime = moment.utc(enrollment.enrollmentToTime);

        const overlapsEnrollment = timeblockStartTime.isBefore(enrollmentEndTime) && enrollmentStartTime.isBefore(timeblockEndTime);

        if (overlapsEnrollment) {
            const availableStartTime = moment.max(timeblockStartTime, enrollmentStartTime);
            const availableEndTime = moment.min(timeblockEndTime, enrollmentEndTime);
            const availableHours = calculateHoursFromDatesWithoutRounding(availableStartTime.toDate(), availableEndTime.toDate());
            totalAvailableHours += availableHours;

            if (enrollment.shift !== null) {
                const performingFrom = moment.utc(enrollment.shift.performingFrom);
                const performingTo = moment.utc(enrollment.shift.to);

                const overlapsScheduled = timeblockStartTime.isBefore(performingTo) && performingFrom.isBefore(timeblockEndTime);

                if (overlapsScheduled) {
                    const scheduledStart = moment.max(timeblockStartTime, performingFrom);
                    const scheduledEnd = moment.min(timeblockEndTime, performingTo);
                    const scheduledHours = calculateHoursFromDatesWithoutRounding(scheduledStart.toDate(), scheduledEnd.toDate());
                    totalScheduledHours += scheduledHours;
                }
            }
        }
    }

    totalAvailableHours = roundToNearestHalfHour(totalAvailableHours);
    totalScheduledHours = roundToNearestHalfHour(totalScheduledHours);

    return `${totalScheduledHours}/${totalAvailableHours}`;
};

export const buildShiftPlanSelections = (event, shiftPlans) => {
    const DAY_WINDOW_SIZE = 1;
    const shiftPlanSelections = [];
    const selectedDayStartTime = moment(event.start);
    const selectedDayEndTime = moment(event.start).add(DAY_WINDOW_SIZE, "days");

    for (let i = 0; i < shiftPlans.length; i++) {
        const shiftPlan = shiftPlans[i];

        if (event.shiftPlanIds.includes(shiftPlan.id)) {
            const shiftPlanStart = moment(shiftPlan.start);
            const shiftPlanEnd = moment(shiftPlan.end);

            if (shiftPlanEnd.isAfter(selectedDayStartTime) && shiftPlanStart.isBefore(selectedDayEndTime)) {
                shiftPlanSelections.push({
                    shiftPlanId: shiftPlan.id,
                    fromTime: shiftPlanStart.isBefore(selectedDayStartTime) ? selectedDayStartTime.toISOString() : shiftPlanStart.toISOString(),
                    toTime: shiftPlanEnd.isAfter(selectedDayEndTime) ? selectedDayEndTime.toISOString() : shiftPlanEnd.toISOString(),
                });
            }
        }
    }
    
    return shiftPlanSelections;
};


export const getPerformerAccreditedServiceTypes = (performerId, performersData) => {
    if (!performersData?.accreditedUsers) {
        return [];
    }

    const performer = performersData.accreditedUsers.find(p => p.id === performerId);
    return performer?.accreditedServiceTypes || [];
};